<template>
  <div class="select-subs">
    <label class="d-label">{{ $t('vue2.substitution.find_sub_for') }} </label>
    <div class="select-subs__header">
      <DoFiltersFilter
        :tags="tags"
        :selectedTags="selectedTags"
        @select="toggleSelectedTags"
        @clear="clearSelectedTags">
      </DoFiltersFilter>
    </div>
    <div class="select-subs__header">
      <input type="search" class="search-user" :placeholder="$t('vue2.common.search_name_or_initials')" @input="debounceSearch">
    </div>
    <ul>
      <li v-if="usersSorted.length === 0" class="italic">{{ $t('vue2.dselect.no_matching_results') }}</li>
      <li
        v-for="(user, index) in usersSorted"
        :key="user.id"
        class="select-subs__sub shadow"
      >
        <div class="select-subs__sub__header">
          <div>
            <p class="select-subs__sub__name">{{ user.name }}</p>
            <p class="select-subs__sub__status__loader linear-background" v-if="isLoadingAvailability"></p>
            <a
              v-else-if="availabilityByCalendarId[user.user_calendar_id]"
              :href="'/calendars/' + user.user_calendar_id"
              target="_blank"
              class="select-subs__sub__status"
              :class="{'select-subs__sub__status--ok': isFullyAvailable(user)}">
              {{ statusText(user) }}
            </a>
          </div>
        </div>
        <button @click="addSub(user)" class="bttn">
          {{ $t('vue2.common.select') }}
        </button>
      </li>
    </ul>
  </div>
</template>

<script>
import {mapGetters} from 'vuex'
import DoFiltersFilter from '../../DoFilters/DoFiltersFilter.vue'
import docendo from '../../../docendo'
import moment from 'moment'

const NON_BLOCKING_EVENT_TYPES = ['presence', 'available']

function isCoveredByPresence (start, end, presences) {
  let cursor = start
  for (const presence of presences) {
    if (presence.start <= cursor && presence.end > cursor) {
      cursor = presence.end
      if (cursor >= end) {
        return true
      }
    }
  }
  return false
}

export default {
  components: {DoFiltersFilter},
  props: {
    selectedEvents: {
      type: Array,
      default: () => []
    }
  },
  data () {
    return {
      users: [],
      isLoading: true,
      searchQuery: '',
      debounceTimer: null,
      tags: [],
      eventsByCalendarId: {},
      isLoadingAvailability: false,
      availabilityTimer: null,
      availabilityRequestId: 0
    }
  },
  computed: {
    ...mapGetters([
      'usersFilteredWithTags',
      'allUsersInCalIdHash',
      'schoolId'
    ]),
    activeAbsentUser () {
      return this.$store.state.subs.activeAbsentUser
    },
    activeUserEvents () {
      return this.$store.state.subs.activeAbsentUserEvents
    },
    filteredUsers () {
      return this.usersFilteredWithTags.filter(
        user => user.user_calendar_id !== this.activeAbsentUser.user_calendar_id
      )
    },
    selectedTags () {
      return this.$store.state.selectedTags || []
    },
    selectedLessons () {
      return this.selectedEvents
        .filter(event => event && event.start && event.end)
        .map(event => ({
          eventId: event.event_id,
          start: moment(event.start).valueOf(),
          end: moment(event.end).valueOf()
        }))
    },
    selectedLessonsKey () {
      return this.selectedLessons.map(lesson => `${lesson.eventId}:${lesson.start}:${lesson.end}`).join(',')
    },
    // { [calendar_id]: { covered: antal lektioner dækket af arbejdstid, busy: antal lektioner med andre aftaler } }
    availabilityByCalendarId () {
      const lessons = this.selectedLessons
      if (lessons.length === 0 || this.isLoadingAvailability) {
        return {}
      }
      const result = {}
      this.filteredUsers.forEach(user => {
        const events = this.eventsByCalendarId[user.user_calendar_id] || []
        const presences = events.filter(event => event.event_type === 'presence')
        const blocking = events.filter(event => NON_BLOCKING_EVENT_TYPES.indexOf(event.event_type) < 0)
        let covered = 0
        let busy = 0
        lessons.forEach(lesson => {
          if (isCoveredByPresence(lesson.start, lesson.end, presences)) {
            covered++
          }
          if (blocking.some(event => event.event_id !== lesson.eventId && event.start < lesson.end && event.end > lesson.start)) {
            busy++
          }
        })
        result[user.user_calendar_id] = {covered, busy}
      })
      return result
    },
    usersSorted () {
      let users = this.filteredUsers
      // Filter the users based on the debounced search query
      if (this.searchQuery) {
        const lowercasedQuery = this.searchQuery.toLowerCase()
        users = users.filter(user => {
          // Check if either name or initials match the search query
          return (
            user.name.toLowerCase().includes(lowercasedQuery) ||
            (user.initials && user.initials.toLowerCase().includes(lowercasedQuery))
          )
        })
      }
      const availability = this.availabilityByCalendarId
      if (Object.keys(availability).length === 0) {
        return users
      }
      return users.slice().sort((a, b) => {
        const statsA = availability[a.user_calendar_id]
        const statsB = availability[b.user_calendar_id]
        return (Number(this.isFullyAvailable(b)) - Number(this.isFullyAvailable(a))) ||
          (statsB.covered - statsA.covered) ||
          (statsA.busy - statsB.busy)
      })
    }
  },
  watch: {
    selectedLessonsKey () {
      clearTimeout(this.availabilityTimer)
      this.availabilityTimer = setTimeout(this.readAvailability, 400)
    }
  },
  methods: {
    toggleSelectedTags (tag) {
      this.$store.commit('setSelectedTags', tag)
    },
    clearSelectedTags () {
      const tagsToRemove = [...this.$store.state.selectedTags]
      tagsToRemove.forEach(tag => {
        this.$store.commit('setSelectedTags', tag)
      })
    },
    addSub (user) {
      if (this.selectedEvents.length < 1) {
        window.alert(this.$t('vue2.substitution.select_lessons_to_substitute'))
        return
      }
      this.$emit('addSub', user)
    },
    isFullyAvailable (user) {
      const stats = this.availabilityByCalendarId[user.user_calendar_id]
      return !!stats && stats.covered === this.selectedLessons.length && stats.busy === 0
    },
    statusText (user) {
      const stats = this.availabilityByCalendarId[user.user_calendar_id]
      const busyText = stats.busy > 0
        ? `${this.$t('vue2.substitution.busy')} ${stats.busy}`
        : this.$t('vue2.substitution.available')
      return `${this.$t('vue2.substitution.working_hours')} ${stats.covered}/${this.selectedLessons.length} · ${busyText}`
    },
    readAvailability () {
      const lessons = this.selectedLessons
      const requestId = ++this.availabilityRequestId
      if (lessons.length === 0) {
        this.eventsByCalendarId = {}
        this.isLoadingAvailability = false
        return
      }
      const start = moment(Math.min(...lessons.map(lesson => lesson.start))).format()
      const end = moment(Math.max(...lessons.map(lesson => lesson.end))).format()
      this.isLoadingAvailability = true
      docendo.schoolAvailabilities(this.schoolId, 'user', start, end, data => {
        if (requestId !== this.availabilityRequestId) {
          return
        }
        const rows = (typeof data === 'string' ? JSON.parse(data) : data) || []
        const eventsByCalendarId = {}
        rows.forEach(row => {
          eventsByCalendarId[row.calendar_id] = row.events.map(event => ({
            event_id: event.event_id,
            event_type: event.event_type,
            start: moment(event.event_time_start).valueOf(),
            end: moment(event.event_time_end).valueOf()
          })).sort((a, b) => a.start - b.start)
        })
        this.eventsByCalendarId = eventsByCalendarId
        this.isLoadingAvailability = false
      }, () => {
        if (requestId === this.availabilityRequestId) {
          this.eventsByCalendarId = {}
          this.isLoadingAvailability = false
        }
      })
    },
    debounceSearch (event) {
      clearTimeout(this.debounceTimer)
      this.debounceTimer = setTimeout(() => {
        this.searchQuery = event.target.value
      }, 400)
    }
  },
  beforeDestroy () {
    // Clean up any active timeouts
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer)
    }
    clearTimeout(this.availabilityTimer)
  },
  mounted () {
    docendo.schoolTags(this.schoolId, data => {
      this.tags = data
    })
  }
}
</script>

<style scoped>
@import "../../../../src/assets/css/variables.css";

.select-subs {
  padding: 1rem;
}

.select-subs__header {
  padding: 1rem 0;
}

.select-subs__sub {
  background: #ffffff;
  padding: 1rem;
  display: flex;
  justify-content: space-between;
  border-bottom: 1px solid var(--border-color);
  border-radius: 4px;
  margin-bottom: 0.5rem;
}

.select-subs__sub__header {
  display: flex;
  align-items: center;
}

.select-subs__sub__header img {
  width: 45px;
  height: 45px;
  border-radius: 4px;
  margin-right: 1rem;
}

.select-subs__sub__name {
  font-weight: bold;
}

input[type="search"].search-user {
  border: 1px solid #acb6be;
  background: #fff;
}

input[type="search"].search-user:focus {
  border: 1px solid #468ad7;
}

.select-subs__sub__status {
  display: block;
  margin-top: 0.25rem;
  font-size: 0.85em;
  font-style: italic;
  color: #6b7785;
}

.select-subs__sub__status--ok {
  color: #2e8b57;
  font-weight: bold;
}

.select-subs__sub__status__loader {
  width: 140px;
  height: 12px;
  margin-top: 0.4rem;
}

.fadeInUp-initial {
  animation-duration: 0.5s;
  animation-fill-mode: both;
}

.list-complete-item-animation {
  transition: all 0.5s;
  width: 100%;
}

.list-complete-enter, .list-complete-leave-to
  /* .list-complete-leave-active below version 2.1.8 */
{
  opacity: 0;
  transform: translateY(30px);
}

.list-complete-leave-active {
  position: absolute;
}

@keyframes placeHolderShimmer {
  0% {
    background-position: -468px 0
  }
  100% {
    background-position: 468px 0
  }
}

.linear-background {
  animation-duration: 1s;
  animation-fill-mode: forwards;
  animation-iteration-count: infinite;
  animation-name: placeHolderShimmer;
  animation-timing-function: linear;
  background: #f6f7f8;
  background: linear-gradient(to right, #eeeeee 8%, #dddddd 18%, #eeeeee 33%);
  background-size: 1000px 104px;
  height: 16px;
  border-radius: 4px;
  position: relative;
  overflow: hidden;
}
</style>



// WEBPACK FOOTER //
// SubsUserPanelSubs2.vue?ff241698